import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Tag, Button, Space, message, Typography, Descriptions,
  Input, Modal, Form, Select, InputNumber, Popconfirm, Divider, Alert,
} from 'antd';
import {
  ApiOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  LinkOutlined, VideoCameraOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, InfoCircleOutlined, KeyOutlined,
  BarChartOutlined, SearchOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

// Cosmos 官转 Seedance 默认模型（对外代号 cngz）。
// 只保留一套价格：input_price 与 metadata.resolution_pricing 直接使用官方刊例价，
// 后台展示、前台展示和实际计费共用同一组数字。
const DEFAULT_MODELS = [
  {
    model_name: 'cngz/seedance-2.0',
    display_name: 'Seedance 2.0',
    model_type: 'video',
    upstream_model_name: 'seedance-2.0',
    input_price: 46,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/api/v2/seedance/domestic/3/task/create',
    status: 'active',
    metadata: JSON.stringify({
      official_model_name: 'doubao-seedance-2-0-260128',
      sort_priority: 100,
      version: '2.0',
      tier: '官转',
      tierColor: 'blue',
      desc: 'Seedance 2.0，纯 URL 素材，无尾帧',
      features: ['官转', '纯 URL 素材', '480p/720p/1080p', '无尾帧'],
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
        { value: '1080p', label: '1080p 超清', disabled: false },
        { value: '4k', label: '4k（价格待核）', disabled: true },
      ],
      durationRange: [4, 15],
      fpsOptions: [24],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      maxRefImages: 10,
      maxRefVideos: 3,
      maxRefAudio: 3,
      assetLibrary: false,
      returnLastFrameSupported: false,
      videoModes: ['full_reference', 'start_end_frames'],
      resolution_pricing: { '480p': 46, '720p': 46, '1080p': 51 },
      resolution_pricing_with_video: { '480p': 28, '720p': 28, '1080p': 31 },
    }),
  },
  {
    model_name: 'cngz/seedance-2.0-fast',
    display_name: 'Seedance 2.0 Fast',
    model_type: 'video',
    upstream_model_name: 'seedance-2.0-fast',
    input_price: 37,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/api/v2/seedance/domestic/3/task/create',
    status: 'active',
    metadata: JSON.stringify({
      official_model_name: 'doubao-seedance-2-0-fast-260128',
      sort_priority: 90,
      version: '2.0 Fast',
      tier: '快速版',
      tierColor: 'green',
      desc: 'Seedance 2.0 Fast，纯 URL 素材，无尾帧',
      features: ['官转', '快速出片', '480p/720p', '无尾帧'],
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
      ],
      durationRange: [4, 15],
      fpsOptions: [24],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      maxRefImages: 10,
      maxRefVideos: 3,
      maxRefAudio: 3,
      assetLibrary: false,
      returnLastFrameSupported: false,
      videoModes: ['full_reference', 'start_end_frames'],
      resolution_pricing: { '480p': 37, '720p': 37 },
      resolution_pricing_with_video: { '480p': 22, '720p': 22 },
    }),
  },
  {
    model_name: 'cngz/seedance-2.0-mini',
    display_name: 'Seedance 2.0 Mini',
    model_type: 'video',
    upstream_model_name: 'seedance-2.0-mini',
    input_price: 23,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/api/v2/seedance/domestic/3/task/create',
    status: 'active',
    metadata: JSON.stringify({
      official_model_name: 'doubao-seedance-2-0-mini-260615',
      sort_priority: 80,
      version: '2.0 Mini',
      tier: '轻量版',
      tierColor: 'orange',
      desc: 'Seedance 2.0 Mini，纯 URL 素材，无尾帧',
      features: ['官转', '轻量', '480p/720p', '无尾帧'],
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
      ],
      durationRange: [4, 15],
      fpsOptions: [24],
      aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', 'adaptive'],
      maxRefImages: 10,
      maxRefVideos: 3,
      maxRefAudio: 3,
      assetLibrary: false,
      returnLastFrameSupported: false,
      videoModes: ['full_reference', 'start_end_frames'],
      resolution_pricing: { '480p': 23, '720p': 23 },
      resolution_pricing_with_video: { '480p': 14, '720p': 14 },
    }),
  },
  {
    model_name: 'cngz/seedance-2.5',
    display_name: 'Seedance 2.5',
    model_type: 'video',
    upstream_model_name: 'seedance-2.5',
    input_price: 70,
    output_price: 0,
    billing_type: 'per_token',
    billing_rate: 0,
    api_path: '/api/v2/seedance/domestic/3/task/create',
    status: 'active',
    metadata: JSON.stringify({
      official_model_name: 'doubao-seedance-2-5-260628',
      sort_priority: 110,
      version: '2.5',
      tier: '旗舰版',
      tierColor: 'red',
      desc: 'Seedance 2.5，最长 30 秒，纯 URL 素材，无尾帧',
      features: ['官转', '最长30秒', '50个多模态参考', '480p/720p', '无尾帧'],
      resolutions: [
        { value: '480p', label: '480p 标清', disabled: false },
        { value: '720p', label: '720p 高清', disabled: false },
      ],
      durationRange: [4, 30],
      fpsOptions: [24],
      aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16', 'adaptive'],
      maxRefImages: 30,
      maxRefVideos: 10,
      maxRefAudio: 10,
      assetLibrary: false,
      returnLastFrameSupported: false,
      videoModes: ['full_reference', 'start_end_frames'],
      resolution_pricing: { '480p': 70, '720p': 70 },
      resolution_pricing_with_video: { '480p': 42, '720p': 42 },
    }),
  },
];

const parseModelMetadata = (model) => {
  if (!model?.metadata) return null;
  try {
    const meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata;
    return {
      version: meta.version || '',
      tier: meta.tier || '',
      tierColor: meta.tierColor || 'default',
      desc: meta.desc || '',
      features: meta.features || [],
      resolutionPricing: meta.resolution_pricing || null,
      officialModelName: meta.official_model_name || '',
      sortPriority: Number.isFinite(Number(meta.sort_priority)) ? Number(meta.sort_priority) : 0,
      minDiscountRate: meta.min_discount_rate,
      fixedDiscountRate: meta.discount_rate,
    };
  } catch {
    return { tier: '', tierColor: 'default', desc: '', features: [] };
  }
};

const getFeatureTags = (model) => parseModelMetadata(model)?.features || [];

const discountLabel = (model) => {
  const meta = parseModelMetadata(model);
  if (typeof meta?.fixedDiscountRate === 'number' && meta.fixedDiscountRate > 0 && meta.fixedDiscountRate <= 1) {
    return <Tag color="purple">固定 {meta.fixedDiscountRate}</Tag>;
  }
  if (typeof meta?.minDiscountRate === 'number' && meta.minDiscountRate >= 1) {
    return <Tag color="volcano">禁止折扣</Tag>;
  }
  if (typeof meta?.minDiscountRate === 'number' && meta.minDiscountRate > 0) {
    return <Tag color="orange">下限 {meta.minDiscountRate}</Tag>;
  }
  return <Tag color="green">跟随 Key</Tag>;
};

const Cosmos = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState(null);
  const [allModels, setAllModels] = useState([]);
  const [models, setModels] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [usageTaskId, setUsageTaskId] = useState('');
  const [taskUsage, setTaskUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const [providerForm] = Form.useForm();
  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [providerEditMode, setProviderEditMode] = useState(false);

  const [modelForm] = Form.useForm();
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [modelEditMode, setModelEditMode] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [discountMode, setDiscountMode] = useState('follow_key');
  const [discountValue, setDiscountValue] = useState(1.0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, modelRes, dashRes] = await Promise.all([
        adminAPI.getProviders(),
        adminAPI.getModelConfigs(),
        adminAPI.getCosmosDashboard().catch(() => null),
      ]);

      let foundProvider = null;
      if (provRes.code === 0) {
        const providers = provRes.data?.list || provRes.data || [];
        // 优先使用对外代号 cngz，兼容历史 cos/cosmos。
        foundProvider = providers.find(p => (p.name || '').toLowerCase() === 'cngz') ||
          providers.find(p =>
            ['cos', 'cosmos'].includes((p.name || '').toLowerCase()) ||
            (p.api_base || '').toLowerCase().includes('sili-cosmos.com')
          ) || null;
      }
      setProvider(foundProvider);

      if (modelRes.code === 0) {
        const list = modelRes.data?.list || modelRes.data || [];
        setAllModels(list);
        setModels(foundProvider ? list.filter(m => m.provider_id === foundProvider.id) : []);
      }

      if (dashRes?.code === 0) setDashboard(dashRes.data);
    } catch (e) {
      console.error('Failed to fetch Cosmos data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateProvider = () => {
    setProviderEditMode(false);
    providerForm.resetFields();
    providerForm.setFieldsValue({
      name: 'cngz',
      display_name: '官方',
      api_base: 'https://api.sili-cosmos.com',
      adapter_type: 'openai',
      provider_type: 'internal',
      status: 'active',
    });
    setProviderModalOpen(true);
  };

  const openEditProvider = () => {
    if (!provider) return;
    setProviderEditMode(true);
    providerForm.setFieldsValue(provider);
    setProviderModalOpen(true);
  };

  const handleProviderSave = async () => {
    try {
      const values = await providerForm.validateFields();
      setSaving(true);
      const res = providerEditMode && provider
        ? await adminAPI.updateProvider(provider.id, { ...provider, ...values })
        : await adminAPI.createProvider(values);
      if (res.code === 0) {
        message.success(providerEditMode ? 'Cosmos 官转供应商已更新' : 'Cosmos 官转供应商已创建');
        setProviderModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('保存失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async () => {
    if (!provider) return;
    try {
      const res = await adminAPI.deleteProvider(provider.id);
      if (res.code === 0) {
        message.success('供应商已删除');
        setProvider(null);
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  const openCreateModel = () => {
    setModelEditMode(false);
    setEditingModel(null);
    setDiscountMode('follow_key');
    setDiscountValue(1.0);
    modelForm.resetFields();
    modelForm.setFieldsValue({
      model_type: 'video',
      provider_id: provider?.id || 0,
      status: 'active',
      input_price: 46,
      output_price: 0,
      billing_type: 'per_token',
      billing_rate: 0,
      api_path: '/api/v2/seedance/domestic/3/task/create',
      sort_priority: 0,
    });
    setModelModalOpen(true);
  };

  const openEditModel = (model) => {
    setModelEditMode(true);
    setEditingModel(model);
    let metadata = model.metadata || '';
    let fixedRate, minRate, sortPriority = 0;
    try {
      const obj = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      fixedRate = obj.discount_rate;
      minRate = obj.min_discount_rate;
      sortPriority = Number.isFinite(Number(obj.sort_priority)) ? Number(obj.sort_priority) : 0;
      const display = { ...obj };
      delete display.discount_rate;
      delete display.min_discount_rate;
      metadata = JSON.stringify(display, null, 2);
    } catch { /* 保留原始文本 */ }

    if (typeof fixedRate === 'number' && fixedRate > 0 && fixedRate <= 1) {
      setDiscountMode('fixed'); setDiscountValue(fixedRate);
    } else if (typeof minRate === 'number' && minRate > 0 && minRate <= 1) {
      setDiscountMode('min'); setDiscountValue(minRate);
    } else {
      setDiscountMode('follow_key'); setDiscountValue(1.0);
    }

    modelForm.setFieldsValue({
      ...model,
      model_type: model.model_type || model.type || 'video',
      provider_id: model.provider_id || provider?.id || 0,
      sort_priority: sortPriority,
      metadata,
    });
    setModelModalOpen(true);
  };

  const applyDiscountToMetadata = (metaStr, sortPriority) => {
    let obj = {};
    if (metaStr) {
      try { obj = JSON.parse(metaStr); } catch { throw new Error('metadata JSON 格式错误'); }
    }
    delete obj.discount_rate;
    delete obj.min_discount_rate;
    obj.sort_priority = Number.isFinite(Number(sortPriority)) ? Number(sortPriority) : 0;
    if (discountMode === 'fixed') {
      if (!(discountValue > 0 && discountValue <= 1)) throw new Error('固定折扣需在 0.01~1.00 之间');
      obj.discount_rate = discountValue;
    } else if (discountMode === 'min') {
      if (!(discountValue > 0 && discountValue <= 1)) throw new Error('折扣下限需在 0.01~1.00 之间');
      obj.min_discount_rate = discountValue;
    }
    return JSON.stringify(obj);
  };

  const handleModelSave = async () => {
    try {
      const values = await modelForm.validateFields();
      setSaving(true);
      let metadata;
      try { metadata = applyDiscountToMetadata(values.metadata, values.sort_priority); }
      catch (e) { message.error(e.message); setSaving(false); return; }

      const data = {
        ...values,
        metadata,
        provider_id: provider?.id || 0,
        provider_name: provider?.name || 'cngz',
      };
      delete data.sort_priority;
      const res = modelEditMode && editingModel
        ? await adminAPI.updateModelConfig(editingModel.id, data)
        : await adminAPI.createModelConfig(data);
      if (res.code === 0) {
        message.success(modelEditMode ? '模型已更新' : '模型已创建');
        setModelModalOpen(false);
        fetchData();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      if (e.errorFields) return;
      message.error('保存失败: ' + (e.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModel = async (model) => {
    try {
      const res = await adminAPI.deleteModelConfig(model.id);
      if (res.code === 0) {
        message.success('模型已删除');
        fetchData();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败: ' + (e.message || ''));
    }
  };

  const handleCreateDefaults = async () => {
    if (!provider) {
      message.warning('请先创建 Cosmos 官转供应商');
      return;
    }
    setSaving(true);
    let created = 0;
    let skipped = 0;
    for (const m of DEFAULT_MODELS) {
      // 同一供应商已有同上游模型（例如历史 cos/... 前缀）时不再重复创建。
      if (allModels.some(x =>
        x.model_name === m.model_name ||
        (x.provider_id === provider.id && x.upstream_model_name === m.upstream_model_name)
      )) {
        skipped++;
        continue;
      }
      try {
        const res = await adminAPI.createModelConfig({
          ...m,
          provider_id: provider.id,
          provider_name: provider.name,
        });
        if (res.code === 0) created++;
      } catch { /* 单个失败不影响其他模型 */ }
    }
    setSaving(false);
    message.success(`创建 ${created} 个模型` + (skipped ? `，跳过 ${skipped} 个已存在` : ''));
    fetchData();
  };

  const handleLookupUsage = async () => {
    const id = usageTaskId.trim();
    if (!id) { message.warning('请输入 Cosmos 内部任务 ID'); return; }
    setUsageLoading(true);
    setTaskUsage(null);
    try {
      const res = await adminAPI.getCosmosTaskUsage(id);
      if (res.code === 0) setTaskUsage(res.data);
      else message.error(res.message || '查询失败');
    } catch (e) {
      message.error('查询失败: ' + (e.message || ''));
    } finally {
      setUsageLoading(false);
    }
  };

  const modelColumns = [
    {
      title: '模型',
      dataIndex: 'model_name',
      width: 220,
      render: (name, record) => {
        const meta = parseModelMetadata(record);
        return (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Text strong>{record.display_name || name}</Text>
              {meta?.tier && <Tag color={meta.tierColor} style={{ fontSize: 11 }}>{meta.tier}</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: 11 }}>{name}</Text>
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (v) => <Tag color={v === 'active' ? 'green' : 'red'}>{v === 'active' ? '启用' : '下架'}</Tag>,
    },
    {
      title: '排序',
      width: 70,
      render: (_, r) => <Text>{parseModelMetadata(r)?.sortPriority ?? 0}</Text>,
    },
    {
      title: '价格',
      width: 210,
      render: (_, r) => {
        const rp = parseModelMetadata(r)?.resolutionPricing;
        return (
          <span style={{ fontWeight: 600, color: '#fa8c16' }}>
            {rp
              ? Object.entries(rp).map(([resolution, price]) => `${resolution} $${price}`).join(' / ')
              : '-'} <small style={{ color: '#888', fontWeight: 400 }}>/1M tokens</small>
          </span>
        );
      },
    },
    { title: '折扣策略', width: 110, render: (_, r) => discountLabel(r) },
    {
      title: '上游模型 / 官方模型',
      dataIndex: 'upstream_model_name',
      width: 240,
      render: (v, r) => {
        const meta = parseModelMetadata(r);
        return (
          <Space direction="vertical" size={0}>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{v || '-'}</Text>
            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>官方 {meta?.officialModelName || '-'}</Text>
          </Space>
        );
      },
    },
    {
      title: '特性',
      width: 220,
      render: (_, record) => {
        const tags = getFeatureTags(record);
        return tags.length ? (
          <Space size={2} wrap>{tags.slice(0, 3).map((f, i) => <Tag key={i} style={{ fontSize: 11 }}>{f}</Tag>)}</Space>
        ) : <Text type="secondary">未配置</Text>;
      },
    },
    {
      title: '操作',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditModel(record)} />
          <Popconfirm title="确定删除此模型？" onConfirm={() => handleDeleteModel(record)} okText="删除" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const configured = dashboard?.configured ?? !!provider;

  return (
    <div style={{ padding: '0 24px 24px', maxWidth: 1400 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <Space align="center" size={12}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #1677ff 0%, #13c2c2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ApiOutlined style={{ fontSize: 20, color: '#fff' }} />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>Cosmos 官转配置</Title>
            <Space size={4}>
              {provider
                ? <Tag color="green" icon={<CheckCircleOutlined />}>已接入</Tag>
                : <Tag color="orange" icon={<CloseCircleOutlined />}>未接入</Tag>}
              {models.length > 0 && <Tag color="blue">{models.length} 个模型</Tag>}
            </Space>
          </div>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>刷新</Button>
      </div>

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="Cosmos 官转 Seedance"
        description="直接传图片 URL，不需要素材库，不支持返回尾帧和智能多帧。配置完成后模型即可调用；余额和任务用量查询不受影响。model_configs 的 resolution_pricing 直接配置官方刊例价，后台、前台和实际计费共用这一套数字；平台折扣单独设置；供应商实际结算和 7.5 折成本可在看板查看。"
        style={{ marginBottom: 20 }}
      />

      {dashboard?.balance_error && <Alert type="warning" showIcon message="上游余额查询失败" description={dashboard.balance_error} style={{ marginBottom: 20 }} />}
      {!configured && (
        <Alert type="warning" showIcon message="尚未配置 Cosmos 官转供应商" description="请先在下方创建供应商并填写 API Key，余额和用量看板才会生效。" style={{ marginBottom: 20 }} />
      )}

      <Card
        title={<Space><KeyOutlined style={{ color: '#1677ff' }} /><span>供应商配置</span></Space>}
        extra={
          provider ? (
            <Space>
              <Button size="small" icon={<EditOutlined />} onClick={openEditProvider}>编辑</Button>
              <Popconfirm title="确定删除此供应商？关联模型将不可用。" onConfirm={handleDeleteProvider} okText="删除" cancelText="取消">
                <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
              </Popconfirm>
            </Space>
          ) : (
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreateProvider}>创建供应商</Button>
          )
        }
        style={{ marginBottom: 20 }}
      >
        {provider ? (
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
            <Descriptions.Item label="上游余额">
              <Text strong style={{ color: '#fa8c16' }}>
                {dashboard?.balance != null ? `¥${Number(dashboard.balance).toFixed(2)} ${dashboard.currency || 'CNY'}` : '-'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="供应商名称"><Text code>{provider.name}</Text></Descriptions.Item>
            <Descriptions.Item label="显示名称">{provider.display_name}</Descriptions.Item>
            <Descriptions.Item label="API 地址"><Space><LinkOutlined /><Text code>已配置（编辑时可见）</Text></Space></Descriptions.Item>
            <Descriptions.Item label="API Key" span={2}>
              <Text code>{provider.api_key ? provider.api_key.substring(0, 10) + '****' + provider.api_key.slice(-4) : '未配置'}</Text>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 30 }}>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>尚未配置 Cosmos 官转供应商。</Paragraph>
            <Text type="secondary" style={{ fontSize: 12 }}>API Base：<Text code>已配置（编辑时可见）</Text></Text>
          </div>
        )}
      </Card>

      <Card
        title={<Space><VideoCameraOutlined style={{ color: '#1677ff' }} /><span>Seedance 视频模型</span><Tag color="blue">{models.length}</Tag></Space>}
        extra={
          <Space>
            <Button size="small" disabled={!provider} onClick={handleCreateDefaults} loading={saving}>一键创建默认模型</Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} disabled={!provider} onClick={openCreateModel}>添加模型</Button>
          </Space>
        }
        style={{ marginBottom: 20 }}
      >
        <Table
          columns={modelColumns}
          dataSource={models}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={false}
          scroll={{ x: 1200 }}
          locale={{ emptyText: '暂无模型，可点击「一键创建默认模型」' }}
        />
      </Card>

      <Card
        title={<Space><BarChartOutlined style={{ color: '#1677ff' }} /><span>上游任务用量查询</span></Space>}
        style={{ marginBottom: 20 }}
      >
        <Space.Compact style={{ width: '100%', maxWidth: 620, marginBottom: 16 }}>
          <Input
            value={usageTaskId}
            onChange={e => setUsageTaskId(e.target.value)}
            placeholder="输入 Cosmos 内部任务 ID（sc-...）"
            onPressEnter={handleLookupUsage}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={usageLoading} onClick={handleLookupUsage}>查询</Button>
        </Space.Compact>

        {taskUsage ? (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
            <Descriptions.Item label="任务 ID"><Text code>{taskUsage.task_id}</Text></Descriptions.Item>
            <Descriptions.Item label="模型">{taskUsage.model || '-'}</Descriptions.Item>
            <Descriptions.Item label="状态">{taskUsage.status || '-'}</Descriptions.Item>
            <Descriptions.Item label="结算状态">{taskUsage.billing_status || '-'}</Descriptions.Item>
            <Descriptions.Item label="实际费用">{((taskUsage.cost || 0) / 100).toFixed(2)} {taskUsage.currency || 'CNY'}</Descriptions.Item>
            <Descriptions.Item label="Tokens">{taskUsage.usage?.total_tokens ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="预扣状态" span={3}>{taskUsage.precharge?.status || '-'}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>输入 Cosmos 内部任务 ID（sc-...）查询用量和费用；v4 对外的 cgt ID 不能直接用于此管理查询。查询失败不代表视频生成失败。</Text>
        )}
      </Card>

      {dashboard?.tasks?.length > 0 && (
        <Card title="最近 Cosmos 任务" style={{ marginBottom: 20 }}>
          <Table
            size="small"
            pagination={false}
            rowKey="task_id"
            dataSource={dashboard.tasks}
            columns={[
              { title: '任务 ID', dataIndex: 'task_id', ellipsis: true, render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
              { title: '模型', dataIndex: 'model', width: 220 },
              { title: '上游状态', dataIndex: 'upstream_status', width: 100, render: v => v || '-' },
              { title: '结算', dataIndex: 'billing_status', width: 100, render: v => v || '-' },
              { title: '费用', dataIndex: 'cost', width: 100, render: v => `¥${((v || 0) / 100).toFixed(2)}` },
              { title: 'Tokens', width: 100, render: (_, r) => r.usage?.total_tokens ?? '-' },
              { title: '错误', dataIndex: 'error', ellipsis: true, render: v => v ? <Text type="danger" style={{ fontSize: 11 }}>{v}</Text> : '-' },
            ]}
          />
        </Card>
      )}

      <Modal
        title={providerEditMode ? '编辑 Cosmos 官转供应商' : '创建 Cosmos 官转供应商'}
        open={providerModalOpen}
        onCancel={() => setProviderModalOpen(false)}
        onOk={handleProviderSave}
        confirmLoading={saving}
        width={560}
        destroyOnHidden
      >
        <Form form={providerForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="供应商名称" rules={[{ required: true, message: '请输入供应商名称' }]}>
            <Input placeholder="cngz" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="官方" />
          </Form.Item>
          <Form.Item name="api_base" label="API Base URL" rules={[{ required: true, message: '请输入 API 地址' }]}>
            <Input placeholder="https://api.example.com" />
          </Form.Item>
          <Form.Item name="api_key" label="API Key" rules={[{ required: true, message: '请输入 API Key' }]}>
            <Input.Password placeholder="sk-..." />
          </Form.Item>
          <Form.Item name="adapter_type" label="适配器类型">
            <Select options={[{ value: 'openai', label: 'OpenAI 兼容' }, { value: 'custom', label: '自定义' }]} />
          </Form.Item>
          <Form.Item name="provider_type" label="供应商类型">
            <Select options={[{ value: 'internal', label: '内部供应商' }, { value: 'external', label: '外部供应商' }]} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '禁用' }]} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={modelEditMode ? '编辑 Cosmos 模型' : '添加 Cosmos 模型'}
        open={modelModalOpen}
        onCancel={() => setModelModalOpen(false)}
        onOk={handleModelSave}
        confirmLoading={saving}
        width={680}
        destroyOnHidden
      >
        <Form form={modelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="model_name" label="公开模型名" rules={[{ required: true, message: '请输入模型名' }]}>
            <Input placeholder="cngz/seedance-2.0" />
          </Form.Item>
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true, message: '请输入显示名称' }]}>
            <Input placeholder="Seedance 2.0" />
          </Form.Item>
          <Form.Item name="upstream_model_name" label="上游模型名" rules={[{ required: true, message: '请输入上游模型名' }]}>
            <Input placeholder="seedance-2.0" />
          </Form.Item>
          <Form.Item name="api_path" label="上游创建路径">
            <Input placeholder="/api/v2/seedance/domestic/3/task/create" />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item
              name="input_price"
              label="基准单价（/1M tokens，720p）"
              rules={[{ required: true }]}
              extra="与官方刊例价一致，作为 720p 基准价"
            >
              <InputNumber min={0} step={0.01} style={{ width: 220 }} />
            </Form.Item>
            <Form.Item
              name="sort_priority"
              label="前端排序优先级"
              extra="数字越大越靠前，写入 metadata.sort_priority"
            >
              <InputNumber min={0} max={100000} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select style={{ width: 120 }} options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '下架' }]} />
            </Form.Item>
          </Space>
          <Form.Item label="平台折扣策略">
            <Space align="start" wrap>
              <Select
                value={discountMode}
                onChange={setDiscountMode}
                style={{ width: 230 }}
                options={[
                  { value: 'follow_key', label: '跟随 API Key 折扣' },
                  { value: 'min', label: '折扣下限' },
                  { value: 'fixed', label: '固定折扣' },
                ]}
              />
              {discountMode !== 'follow_key' && (
                <InputNumber
                  value={discountValue}
                  onChange={v => setDiscountValue(v ?? 1)}
                  min={0.01}
                  max={1}
                  step={0.01}
                  style={{ width: 130 }}
                  addonBefore={discountMode === 'fixed' ? '固定' : '下限'}
                />
              )}
            </Space>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
              跟随 Key：客户自己的折扣生效；下限：客户折扣不能更优惠；固定：覆盖客户折扣。折扣不写进下方 metadata。
            </Text>
          </Form.Item>
          <Form.Item
            name="metadata"
            label="模型特性 / 分辨率 / 定价 JSON"
            extra="resolution_pricing 是实际计费价（/1M tokens），后台、前台和计费共用；含参考视频用 resolution_pricing_with_video。official_model_name 用于 v4 对外返回官方模型 ID；sort_priority 控制前端视频模型排序（越大越靠前）。"
          >
            <Input.TextArea rows={12} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
          <Form.Item name="model_type" hidden><Input /></Form.Item>
          <Form.Item name="provider_id" hidden><InputNumber /></Form.Item>
          <Form.Item name="billing_type" hidden><Input /></Form.Item>
          <Form.Item name="output_price" hidden><InputNumber /></Form.Item>
          <Form.Item name="billing_rate" hidden><InputNumber /></Form.Item>
        </Form>
      </Modal>

      <Divider />
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        默认模型包含 seedance-2.0 / 2.0-fast / 2.0-mini / 2.5，模型名为 cngz/ 前缀，上游请求仍使用 seedance-*。上游不提供素材库、尾帧输出、智能多帧、取消/回调接口；v4 对外使用官方 cgt 任务 ID 和官方模型 ID，列表接口来自平台本地任务记录。
      </Paragraph>
    </div>
  );
};

export default Cosmos;
